import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'src/hooks';
import { links } from '../chrome';
import { OncoreFlexContextQuery, OncoreFlexContextQuery$data as ContextsResponse } from '../../__generated__/OncoreFlexContextQuery.graphql';
import { fetchQuery } from 'relay-runtime';
import environment from '../../environment';
import Query from '../../hooks/OncoreFlexContextQuery';
import { PropsWithChildren, useEffect } from 'react';
import { setContexts, setErrorContext } from '../../store/appSlice';
import { useSetAnalyticsUserProperties } from '@oncore/shared';
import OncoreLoader from '@oncore/ui/shared/OncoreLoader';
import { UnsavedChangesProvider } from '@oncore/ui/shared/form';

const getContexts = (): Promise<ContextsResponse | undefined> => {
  return fetchQuery<OncoreFlexContextQuery>(
    environment,
    Query,
    {}
  ).toPromise();
};

export type Props = {
  children?: never;
};

type AuthContentProps = PropsWithChildren<{
  pathName: string;
  isMultiFactorAuthEnabled: boolean;
}>

const AuthContent = (props: AuthContentProps) => {
  const {
    children,
    pathName,
    isMultiFactorAuthEnabled,
  } = props
  
  const navigate = useNavigate();

  useEffect(() => {
    if (!isMultiFactorAuthEnabled) {
      const isMfaRedirectPage = pathName.includes(links.redirectMfa);
      const isMfaEnrollPage = pathName.includes(links.enrollMfa);
      if (isMfaRedirectPage || isMfaEnrollPage) {
        return;
      } else {
        navigate(links.redirectMfa);
      }
    }
  }, [isMultiFactorAuthEnabled, pathName])

  return children;
}

export const AuthGuard = (_: Props) => {

  const location = useLocation();
  const dispatch = useDispatch();
  const { 
    token, 
    security,
    isContextStale, 
    currentContext, 
  } = useSelector((s) => s.app);

  const isMultiFactorAuthEnabled = security.isMultiFactorAuthenticationEnabled;
  const setAnalyticsUserProperties = useSetAnalyticsUserProperties();

  useEffect(() => {
    if (token && isContextStale) {
      getContexts()
        .then((response) => response?.user && dispatch(setContexts(response)))
        .catch((error) => dispatch(setErrorContext(error)))
        .finally(() => {
          if (currentContext.context === 'agency' || currentContext.context === 'enterprise') {
            setAnalyticsUserProperties({
              tenant: currentContext.displayName,
              context: currentContext.context,
              features: currentContext.features,
              permissions: currentContext.permissions
            });
          } else if (currentContext.context === 'user') {
            setAnalyticsUserProperties({
              tenant: 'none',
              context: currentContext.context,
              features: currentContext.features,
              permissions: currentContext.permissions
            });
          }
        });
    }
  }, [token, isContextStale]);

  if (!token) {
    return (
      <Navigate to={links.home} state={location} />
    );
  }

  if (currentContext.context === 'loading') {
    return (
      <OncoreLoader 
        variant="splash"
        loadingText="Fetching user data..."/>
    );
  }

  return (
    <UnsavedChangesProvider>
      <AuthContent
        pathName={location.pathname}
        isMultiFactorAuthEnabled={isMultiFactorAuthEnabled}
      >
        <Outlet />
      </AuthContent>
    </UnsavedChangesProvider>
  );
};

export default AuthGuard;
